import { connect, useSelector } from 'react-redux';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import React from 'react';
import { useAppDispatch } from '../../../../commons/store/hooks';
import { RootState } from '../../../../commons/reducers/rootReducer';
import {
  closeRecommissioningReportDialog,
  RecommissioningReportFormValues,
  removePendingRecommissioningReport,
  submitRecommissioningReportDialog,
  updateRecommissioningReport,
  validateRecommissioningReportForm,
} from './wagonRecommissioningReportDucks';
import { getWagonRecommissioningReportState } from '../../../../commons/selectors/selectors';
import DateTimeField from '../../../../commons/components/input/DateTimeField';
import './recommissioningReportDialogStyles.scss';
import Textarea from '../../../../commons/components/input/Textarea';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import '../../../../commons/styles/dropzone.scss';
import { AppDispatch } from '../../../../commons/store/store';
import { startToast } from '../../../../commons/components/toast/toastDucks';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mo
type Props = InjectedFormProps<RecommissioningReportFormValues>;

const RecommissioningReportDialog = ({ initialValues, handleSubmit }: Props) => {
  const dispatch = useAppDispatch();
  const dialogState = useSelector(getWagonRecommissioningReportState);
  const forCancellation = dialogState.dialogType === 'CANCEL';
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    onDrop: (acceptedFiles: File[], fileRejections) => {
      if (fileRejections && fileRejections.length > 0) {
        fileRejections.forEach((file) => {
          file.errors.forEach((err) => {
            dispatch(
              startToast({
                text:
                  err.code === 'file-too-large'
                    ? 'La taille du fichier a dépassé la limite maximale de 5 Mo.'
                    : 'Une erreur a survenu lors du téléchargement de ce file: ' + err.message,
                className: 'error',
              }),
            );
          });
        });
        return;
      }

      dispatch(updateRecommissioningReport({ acceptedFiles }));
    },
  });

  const saveReportDate = (_: any, propertyToUpdate: any) => (dispatch: AppDispatch) => {
    dispatch(updateRecommissioningReport({ reportDate: propertyToUpdate.reportDate }));
  };

  const handleCancel = () => {
    dialogState.onClose();
    dispatch(closeRecommissioningReportDialog());
  };

  const submitRecommissioningReport = (formValues: RecommissioningReportFormValues) => {
    dispatch(submitRecommissioningReportDialog(formValues, dialogState.dialogType));
  };

  const getTitle = () => {
    switch (dialogState.dialogType) {
      case 'EDIT_REPORT_FILE':
        return 'Uploader le rapport ARES';
      case 'CREATION':
        return 'Déclarer une remise en service';
      case 'CANCEL':
        return 'Annuler la déclaration';
    }
  };

  return (
    <form onSubmit={handleSubmit(submitRecommissioningReport)}>
      <div className="recommissioning-report-dialog">
        <div className="dialog-fade" onClick={handleCancel} />
        <div className="dialog-body">
          <h1 className="dialog-title">{getTitle()}</h1>

          <div className="dialog-form">
            <DateTimeField
              className="report-date"
              name="reportDate"
              labelText="Date de remise en service *"
              data={initialValues}
              disabled={forCancellation}
              saveAction={saveReportDate}
            />
            {initialValues.reportFileName && !dialogState.formData.pendingFile && (
              <div className="report-file-div">
                <span className="file-name">{initialValues.reportFileName}</span>
              </div>
            )}
            {dialogState.formData.pendingFile && (
              <div className="report-file-div">
                <span className="file-name">{dialogState.formData.pendingFile.name}</span>
                <span
                  className="btn-icon remove"
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(removePendingRecommissioningReport());
                  }}
                />
              </div>
            )}
            {!dialogState.formData.pendingFile && dialogState.dialogType !== 'CANCEL' && (
              <div
                {...getRootProps()}
                className={classNames('drop-zone', {
                  reject: isDragReject,
                  accept: isDragAccept,
                  active: isDragActive,
                })}
              >
                <input {...getInputProps()} />
                {isDragReject ? <>Impossible isDragReject</> : null}
                {isDragActive ? (
                  <span>Déposer l'annexe ici</span>
                ) : (
                  <span>
                    Déposez le rapport ici,
                    <br />
                    ou cliquez pour sélectionner le rapport à ajouter
                  </span>
                )}
              </div>
            )}

            {forCancellation && (
              <Field
                component={Textarea}
                data={initialValues}
                labelText="Commentaire d'annulation *"
                name="cancelComment"
                placeholder="Veuillez expliquer la raison d'annulation"
                type="text"
                maxLength={255}
              />
            )}
          </div>
          <div className="btn-group">
            <button type="submit" className="btn btn-success">
              Enregistrer
            </button>
            <button type="button" className="btn btn-link" onClick={handleCancel}>
              Annuler
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialValues: getWagonRecommissioningReportState(state).formData,
});

const RecommissioningReportForm = reduxForm<RecommissioningReportFormValues>({
  form: 'recommissioningReportForm',
  validate: validateRecommissioningReportForm,
  enableReinitialize: true,
})(RecommissioningReportDialog);

export default connect(mapStateToProps)(RecommissioningReportForm);
